import * as THREE from 'three';
import { startKliStage } from './kli/stage.js';
import * as A from './kli/accessories.js';
import * as P from './kli/props.js';
import { callPhone, voiceWaves } from './kli/callprops.js';

/*
 * gocall.live — the voice-agent landing, in the kaliai.tech design language.
 * Kli is the voice agent: headset on, sound waves while she talks, a phone on a live call.
 */

/* What each [data-kli] window shows ------------------------------------------ */
// frame: [centerX, centerY, width, height] of the world box the camera must fit
const CAST = {
  hero:      { shells: 44, frame: [-0.85, 0.3, 4.9, 3.3],
               dress: (k) => ({ headset: A.headset(k), waves: voiceWaves(k),
                                sparkles: P.sparkles(k, [[-1.45, 1.05, 0.1, 0.26], [1.75, 0.15, 0.1, 0.18], [1.55, 1.35, -0.2, 0.14]]) }) },
  demo:      { shells: 40, frame: [0.6, 0.3, 4.4, 3.4], dress: (k) => ({ headset: A.headset(k), phone: callPhone(k, { mode: 'ring', name: 'שיחה מ-GoCall' }) }) },
  voice:     { frame: [-0.15, 0.1, 4.0, 2.9], dress: (k) => ({ headset: A.headset(k) }) },
  calendar:  { frame: [0.65, 0.15, 4.5, 3.0], dress: (k) => ({ calendar: P.calendar(k) }) },
  talk:      { frame: [0.6, 0.1, 4.2, 2.9], dress: (k) => ({ phone: callPhone(k), waves: voiceWaves(k, { x: -1.2, y: 0.3 }) }) },
  transfer:  { frame: [0.6, 0.1, 4.3, 2.9], dress: (k) => ({ headset: A.headset(k), rings: A.rings(k) }) },
  leads:     { frame: [0.65, 0.1, 4.4, 2.9], dress: (k) => ({ glasses: A.glasses(k), cards: A.contactCards(k) }) },
  knowledge: { frame: [0.5, 0.25, 4.2, 3.1], dress: (k) => ({ cap: P.gradCap(k), books: P.books(k) }) },
  reports:   { frame: [0.6, 0.15, 4.3, 3.0], dress: (k) => ({ chart: P.chart(k) }) },
  flow:      { frame: [0.7, 0.15, 4.6, 3.0], dress: (k) => ({ flow: P.flowGraph(k) }) },
  setup:     { frame: [0.4, 0.3, 4.0, 3.2], dress: (k) => ({ cap: P.gradCap(k), books: P.books(k) }) },
  connect:   { frame: [0.6, 0.15, 4.2, 3.0], dress: (k) => ({ phone: callPhone(k, { mode: 'ring', name: 'המספר של העסק' }) }) },
  live:      { frame: [0.0, 0.45, 4.0, 3.5], dress: (k) => ({ party: A.partyHat(k), headset: A.headset(k), confetti: A.confetti(k) }) },
  industry:  { shells: 40, frame: [0.35, 0.3, 4.0, 3.3], dress: (k) => ({
    headset: A.headset(k),
    bow: P.headBow(k), band: P.sweatband(k), mirror: P.headMirror(k), glasses: A.glasses(k), chart: P.chart(k),
    bag: P.shoppingBag(k), hardhat: A.hardhat(k), wrench: A.wrench(k), bowtie: A.bowtie(k) }) },
  top:       { shells: 24, frame: [0.4, 0.6, 3.0, 3.7], dress: (k) => ({ balloon: A.balloon(k) }) },
};

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const wait = (ms) => new Promise((r) => setTimeout(r, reducedMotion ? 0 : ms));

/* Hero: the call transcript drives Kli (waves while she talks, hops on an action) */
const SCRIPT = [
  ['agent', 'דנה', 'היי, כאן דנה ממספרת שירן. איך אפשר לעזור?'],
  ['caller', 'לקוח', 'רציתי לקבוע תספורת, אולי מחר אחרי הצהריים?'],
  ['action', '', 'בודקת זמינות ביומן…'],
  ['agent', 'דנה', 'מחר יש 16:30 או 18:00. מה מתאים לך?'],
  ['caller', 'לקוח', 'רגע, בעצם עדיף ביום חמישי'],
  ['agent', 'דנה', 'אין בעיה. ביום חמישי יש 17:00. לקבוע?'],
  ['caller', 'לקוח', 'כן, מעולה'],
  ['action', '', 'התור נקבע ✓ תזכורת נשלחה בוואטסאפ'],
];
async function runCall(view) {
  const body = document.getElementById('call-body');
  const card = body.closest('.call');
  const clock = document.getElementById('call-state');
  const { kli, outfit } = view;
  const add = ([cls, who, text]) => {
    const li = document.createElement('li');
    li.className = cls;
    if (who) li.append(Object.assign(document.createElement('b'), { textContent: who }));
    li.append(text);
    body.append(li);
    return li;
  };
  const toCall = new THREE.Vector2(-0.85, 0.35);   // Kli glances at the transcript
  let seconds = 0;
  setInterval(() => { seconds++; clock.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`; }, 1000);

  if (reducedMotion) { SCRIPT.forEach(add); return; }
  for (;;) {
    body.replaceChildren();
    seconds = 0;
    for (const line of SCRIPT) {
      const [cls, , text] = line;
      if (cls === 'agent') {
        outfit.waves.setOn(true);
        card.classList.add('talking');
        outfit.headset.poke();
        add(line);
        await wait(700 + text.length * 45);
        outfit.waves.setOn(false);
        card.classList.remove('talking');
        await wait(500);
      } else if (cls === 'caller') {
        add(line);
        view.lookOverride = toCall;
        await wait(900 + text.length * 35);
        view.lookOverride = null;
      } else {
        add(line);
        kli.jump(4);
        outfit.sparkles.poke();
        await wait(1100);
      }
    }
    await wait(4500);
  }
}

/* Demo: details → WhatsApp code → the agent calls -------------------------------- */
// Plain fetch on purpose — the app's apiClient has a 401 interceptor that would log
// out a staff session open in another tab.
const API = (import.meta.env.VITE_API_BASE ?? '/api/v1/merkaziya').replace(/\/$/, '');
let celebrate = () => {};
async function post(path, payload) {
  const res = await fetch(`${API}/voice-demo/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  return { status: res.status, body: await res.json().catch(() => ({})) };
}
function messageFor({ status, body }) {
  if (typeof body.message === 'string' && body.message) return body.message;
  if (status === 422) return 'בדקו את הפרטים ונסו שוב.';
  if (status === 429) return 'יותר מדי ניסיונות. נסו שוב בעוד כמה דקות.';
  return 'משהו השתבש. נסו שוב בעוד רגע.';
}
function setupDemo() {
  const details = document.getElementById('step-details');
  const codeForm = document.getElementById('step-code');
  const calling = document.getElementById('step-calling');
  const masked = document.getElementById('masked-phone');
  let phone = '';
  const show = (step) => {
    for (const s of [details, codeForm, calling]) s.hidden = s !== step;
    (step.querySelector('input:not([type=checkbox])') ?? step).focus({ preventScroll: true });
  };
  const say = (form, text) => { const el = form.querySelector('.form-status'); el.textContent = text; el.hidden = !text; };
  const busy = (form, on, label) => {
    const btn = form.querySelector('button[type=submit]');
    if (on) { btn.dataset.label = btn.textContent; btn.textContent = label; } else if (btn.dataset.label) btn.textContent = btn.dataset.label;
    btn.disabled = on;
  };
  const ok = (r) => r.status >= 200 && r.status < 300 && r.body.ok !== false;

  details.addEventListener('submit', async (e) => {
    e.preventDefault();
    say(details, '');
    if (!details.reportValidity()) return;
    const data = Object.fromEntries(new FormData(details));
    delete data.consent;
    busy(details, true, 'שולחים קוד…');
    try {
      const r = await post('request-code', data);
      if (ok(r)) {
        phone = data.phone;
        masked.textContent = typeof r.body.phone_masked === 'string' ? r.body.phone_masked : data.phone;
        show(codeForm);
      } else say(details, messageFor(r));
    } catch { say(details, 'אין חיבור כרגע. בדקו את האינטרנט ונסו שוב.'); } finally { busy(details, false); }
  });

  const codeInput = codeForm.querySelector('input[name=code]');
  codeInput.addEventListener('input', () => {
    codeInput.value = codeInput.value.replace(/\D/g, '').slice(0, 6);
    if (codeInput.value.length === 6) codeForm.requestSubmit();
  });
  codeForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    say(codeForm, '');
    if (!/^\d{6}$/.test(codeInput.value)) { say(codeForm, 'הקוד בן 6 ספרות.'); return; }
    busy(codeForm, true, 'מחייגים…');
    try {
      const r = await post('call', { phone, code: codeInput.value });
      if (ok(r)) { show(calling); celebrate(); } else {
        say(codeForm, messageFor(r));
        if (r.status === 422) { codeInput.value = ''; codeInput.focus(); }
      }
    } catch { say(codeForm, 'אין חיבור כרגע. בדקו את האינטרנט ונסו שוב.'); } finally { busy(codeForm, false); }
  });
  document.getElementById('back-to-details').addEventListener('click', () => { codeInput.value = ''; say(codeForm, ''); show(details); });
}
function setupDemoKli(view) {
  const conf = view.kli.add(A.confetti(view.kli));
  conf.visible = false;
  celebrate = () => {
    view.outfit.phone.answer();
    conf.visible = true;
    conf.poke();
    view.kli.jump(5);
  };
}

/* Industries: one Kli, a different outfit per business ---------------------- */
const INDUSTRIES = {
  beauty: { name: 'קוסמטיקה וטיפוח', wear: ['bow'], does: ['עונה לכל שיחה גם כשהמטפלת באמצע טיפול', 'קובעת ומזיזה תורים לפי היומן', 'מתקשרת יום לפני לאשר הגעה ומצמצמת ביטולים'] },
  gym: { name: 'מכוני כושר', wear: ['band'], does: ['מחייגת לכל ליד מהפייסבוק תוך שניות', 'מתאמת אימון ניסיון', 'מתקשרת למנויים שמסיימים לחדש'] },
  clinic: { name: 'מרפאות', wear: ['mirror'], does: ['מנהלת את קו התורים בלי המתנה', 'עונה על שאלות לפני ואחרי טיפול', 'מעבירה פנייה דחופה לצוות מיד'] },
  accounting: { name: 'רואי חשבון', wear: ['glasses', 'chart'], does: ['עונה על שאלות שגרתיות של לקוחות', 'מתקשרת להזכיר מועדי הגשה ומסמכים חסרים', 'מסננת ומעבירה לרו״ח רק מה שצריך'] },
  shop: { name: 'חנויות ומשלוחים', wear: ['bag'], does: ['עונה על זמינות, מחירים ומשלוחים', 'מעדכנת את הלקוח איפה ההזמנה', 'מחייגת לאשר כתובת ושעת מסירה'] },
  garage: { name: 'מוסכים', wear: ['hardhat', 'wrench'], does: ['קובעת טיפולים ותורים לטסט', 'מתקשרת כשהרכב מוכן', 'מקריאה הצעת מחיר ומקבלת אישור בטלפון'] },
  law: { name: 'משרדי עורכי דין', wear: ['bowtie', 'glasses'], does: ['מסננת פניות ראשונות ואוספת פרטי תיק', 'קובעת פגישות ייעוץ', 'מתקשרת להזכיר דיונים ומסמכים'] },
  callcenter: { name: 'מוקדי שירות ומכירות', wear: ['bowtie', 'chart'], does: ['עונה ראשונה בעומס ומקצרת את זמני ההמתנה', 'מסננת ומעבירה לתור הנכון עם סיכום לנציג', 'מחייגת בקמפיינים לצד הנציגים, וכל שיחה נמדדת'] },
};
function setupIndustries(view) {
  const chips = document.querySelectorAll('[data-ind]');
  const name = document.getElementById('ind-name');
  const list = document.getElementById('ind-list');
  const pick = (key) => {
    const ind = INDUSTRIES[key];
    Object.entries(view.outfit).forEach(([k, a]) => { if (k !== 'headset') a.visible = ind.wear.includes(k); });
    chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.ind === key)));
    name.textContent = ind.name;
    list.replaceChildren(...ind.does.map((d) => Object.assign(document.createElement('li'), { textContent: d })));
    view.kli.jump();
  };
  chips.forEach((c) => c.addEventListener('click', () => pick(c.dataset.ind)));
  pick('beauty');
}
// Text for the chips works even without WebGL.
function industriesFallback() {
  const list = document.getElementById('ind-list');
  if (!list.children.length) list.replaceChildren(...INDUSTRIES.beauty.does.map((d) => Object.assign(document.createElement('li'), { textContent: d })));
}

/* Mobile menu ------------------------------------------------------------------- */
function setupMenu() {
  const toggle = document.querySelector('.nav-toggle');
  const menu = document.getElementById('nav-menu');
  const set = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'סגירת תפריט' : 'פתיחת תפריט');
    menu.hidden = !open;
  };
  toggle.addEventListener('click', () => set(menu.hidden));
  menu.addEventListener('click', (e) => { if (e.target.closest('a')) set(false); });
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) { set(false); toggle.focus(); } });
  matchMedia('(min-width: 821px)').addEventListener('change', (e) => { if (e.matches) set(false); });
}

/* Enterprise: the Kli team scene (its own renderer + a 1.6MB model) loads only when
   the section comes near — phones that never scroll there never download it. */
function lazyTeam() {
  const stage = document.getElementById('team-stage');
  if (!stage) return;
  new IntersectionObserver(([e], io) => {
    if (!e.isIntersecting) return;
    io.disconnect();
    import('./kli/team.js').catch((err) => console.error(err));
  }, { rootMargin: '600px 0px' }).observe(stage);
}

/* Boot -------------------------------------------------------------------------- */
lazyTeam();
setupMenu();
setupDemo();
industriesFallback();
startKliStage({ canvas: document.getElementById('kli-canvas'), cast: CAST }).then((stage) => {
  runCall(stage.find('hero'));
  setupDemoKli(stage.find('demo'));
  setupIndustries(stage.find('industry'));
  const top = stage.find('top').el;
  top.addEventListener('click', () => scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' }));
  // Shown once the hero is gone, hidden again over the footer so it never covers its links
  const seen = new Map();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => seen.set(e.target, e.isIntersecting));
    top.classList.toggle('show', ![...seen.values()].some(Boolean));
  });
  io.observe(document.querySelector('.hero'));
  io.observe(document.querySelector('.footer'));
}).catch((err) => {
  console.error(err);
  document.body.classList.add('klis-failed');
});
